// GTranslate float widget for agntInc. Mounted once in `_app` (not the per-page
// layout) so it survives client-side navigation — `float.js` builds the switcher
// only on execution and won't rebuild a re-mounted, empty wrapper.

import { useEffect } from 'react';

import styles from './style.module.scss';

const WIDGET_ID = 'agnt';
const WRAPPER_ID = 'gt-wrapper-agnt';
const FLOAT_SCRIPT_SRC = 'https://cdn.gtranslate.net/widgets/latest/float.js';
const FLOAT_SCRIPT_ID = 'gtranslate-float';

const DEFAULT_LANGUAGE = 'en';
const LANGUAGES = ['ar', 'en', 'fr', 'de', 'el', 'iw', 'it', 'pl', 'pt', 'es', 'tr'];

const SETTINGS = {
  default_language: DEFAULT_LANGUAGE,
  languages: LANGUAGES,
  url_structure: 'none',
  flag_style: '2d',
  wrapper_selector: `#${WRAPPER_ID}`,
  alt_flags: { en: 'usa' },
  float_switcher_open_direction: 'bottom',
  switcher_horizontal_position: 'right',
  switcher_vertical_position: 'top',
};

function applyLangFromQueryParam() {
  const lang = new URLSearchParams(window.location.search).get('lang');
  if (!lang || !LANGUAGES.includes(lang)) return;

  const value = `googtrans=/${DEFAULT_LANGUAGE}/${lang};path=/`;
  const { hostname } = window.location;
  // Set host-scoped and registrable-domain-scoped so the selection survives
  // across subdomains (float.js itself writes the domain-scoped variant).
  document.cookie = value;
  const registrableDomain = hostname.split('.').slice(-2).join('.');
  if (registrableDomain && registrableDomain !== hostname) {
    document.cookie = `${value};domain=.${registrableDomain}`;
  }
}

declare global {
  interface Window {
    gtranslateSettings?: Record<string, unknown>;
  }
}

export default function GTranslate() {
  useEffect(() => {
    window.gtranslateSettings = window.gtranslateSettings ?? {};
    window.gtranslateSettings[WIDGET_ID] = SETTINGS;

    // Pin the language from `?lang=` before float.js runs so it translates on load.
    applyLangFromQueryParam();

    // Load float.js once; it builds the switcher into the wrapper on execution.
    if (!document.getElementById(FLOAT_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = FLOAT_SCRIPT_ID;
      script.src = FLOAT_SCRIPT_SRC;
      script.defer = true;
      script.setAttribute('data-gt-widget-id', WIDGET_ID);
      document.body.appendChild(script);
    }
  }, []);

  return <div className={`gtranslate_wrapper ${styles.wrapper}`} id={WRAPPER_ID} />;
}
